'use client';

import { Image } from '@packages/shared/src/ui/Image/Image';
import { defineMessages, useIntl } from 'react-intl';

import { Chip } from '@packages/shared/src/ui/Chip/Chip';
import { RatingStars } from '@packages/shared/src/ui/Rating/RatingStars';
import type { FragmentType } from '@packages/gql/generated/shopping';
import { unmask } from '@packages/gql/src/betterMasking';
import type { SelectedFilterValueChipFragmentFragmentDoc } from '@packages/gql/generated/shopping/SelectedFilterValueChipFragmentFragmentDoc';
import type { MessageValue } from 'react-intl';

export const messages = defineMessages<{
  readonly deleteLabel: { readonly name: MessageValue };
  readonly deleteLabelRating: { readonly name: MessageValue; readonly rating: MessageValue };
}>({
  deleteLabel: {
    id: 'search.filters.chips.delete',
    defaultMessage: 'Remove filtering for {name}',
  },
  deleteLabelRating: {
    id: 'search.filters.chips.deleteRating',
    defaultMessage: 'Remove filter for rating {rating} stars {name}', // NOTE: name for rating values is usually just "& mehr" or something similar
  },
});

/* GraphQL */ `
  fragment SelectedFilterValueChipFragment on SearchFilterValueBase {
    name
    # currently unsupported in chip
    # ... on SearchColorFilterValue {
    #   color
    # }
    ... on SearchImageFilterValue {
      image
    }
    ... on SearchRatingFilterValue {
      rating
    }
  }
`;

export type SelectedFilterValueChipProps = {
  maskedData: FragmentType<typeof SelectedFilterValueChipFragmentFragmentDoc>;
  showColors?: boolean;
  onDelete: () => void;
};

/**
 * SelectedFilterValueChip component description displayed in storybook
 * */
export const SelectedFilterValueChip = ({
  maskedData,
  showColors,
  onDelete,
}: SelectedFilterValueChipProps) => {
  const { formatMessage } = useIntl();
  const value = unmask<typeof SelectedFilterValueChipFragmentFragmentDoc>(maskedData);

  const { name } = value;
  const image = 'image' in value && value.image;
  const rating = 'rating' in value ? value.rating : undefined;

  return (
    <Chip
      onDelete={onDelete}
      variant="filled"
      ariaLabelForDelete={
        rating
          ? formatMessage(messages.deleteLabelRating, { name, rating })
          : formatMessage(messages.deleteLabel, { name })
      }
    >
      <span className="flex items-center gap-0.5">
        {rating && <RatingStars ratingValue={rating} className="pl-0.5" />}
        {!rating && showColors && image && (
          <Image
            className="border-color-grey size-3 rounded-full border-2"
            width={24}
            height={24}
            src={image}
            alt={name}
          />
        )}
        <span className="pr-1 pl-0.5 first:pl-1">{name}</span>
      </span>
    </Chip>
  );
};
